import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// import { createPortal } from 'react-dom';
import useGameBase from '../../hooks/useGameBase';
import SpectatorPerspectiveSelector from "./SpectatorPerspectiveSelector";
import { useSound } from '../../utils/sound/SoundProvider';

const EMPTY_GRID = Array.from({ length: 9 }, () => Array(9).fill(0));

function getCellKey(row, col) {
  return `${row}-${col}`;
}


function SudokuGame({ roomId, gameState, gamePlayers = [], user = {}, socket, onGameAction, isSpectator = false }) {
  const sound = useSound();

  // --- Spectator Perspective logic (modular, opt-in, matches Canasta) ---
  const perspectiveOptions = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  // useGameBase with perspectivePlayer
  const { submitAction, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId, perspectivePlayer });

  // For all per-player state, use the selected perspective
  const players = Array.isArray(gameState?.players) ? gameState.players : gamePlayers;
  // Find the "current" player id for this perspective
  const myId = useMemo(() => {
    // Use the first valid id from perspectiveIds that matches a playerProgress entry
    for (const id of perspectiveIds) {
      if (gameState?.playerProgress && gameState.playerProgress[id]) return id;
    }
    // fallback to socket/user
    return socket?.id || user?.socketId || user?.userId || null;
  }, [perspectiveIds, gameState?.playerProgress, socket?.id, user?.socketId, user?.userId]);

  const [selectedCell, setSelectedCell] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [floatingPad, setFloatingPad] = useState(null);
  // Pencil (draft) mode
  const [draftMode, setDraftMode] = useState(false);
  // Per-cell small numbers: { 'row-col': [nums in order] }
  const [cornerMarks, setCornerMarks] = useState({});
  const lastMoveRef = useRef(null);
  const boardContainerRef = useRef(null);
  // const padRef = useRef(null);
  const cellRefs = useRef({});

  const dispatchAction = useCallback(async (action) => {
    if (typeof onGameAction === 'function') {
      return onGameAction(action);
    }
    return submitAction(action);
  }, [onGameAction, submitAction]);

  const fixedCells = useMemo(() => gameState?.puzzle?.fixedCells || {}, [gameState?.puzzle?.fixedCells]);
  const playerProgress = useMemo(() => gameState?.playerProgress || {}, [gameState?.playerProgress]);
  // Use the perspective for all per-player state
  const myProgress = myId ? playerProgress[myId] || null : null;
  const grid = myProgress?.grid?.length === 9 ? myProgress.grid : gameState?.puzzle?.initialGrid || EMPTY_GRID;
  const conflicts = useMemo(() => new Set(myProgress?.conflicts || []), [myProgress?.conflicts]);
  const finished = !!gameState?.finished;
  const winners = useMemo(() => (Array.isArray(gameState?.winners) ? gameState.winners : []), [gameState?.winners]);
  // const winnerNames = winners.map((winnerId) => {
  //   const player = players.find((entry) => (entry.socketId || entry.userId || entry.id) === winnerId);
  //   return player?.username || 'Player';
  // });

  // Progress rows: always show all players, but highlight the perspective
  const progressRows = useMemo(() => {
    return players.map((player) => {
      const playerId = player.socketId || player.userId || player.id;
      const progress = playerProgress[playerId] || { filledCells: 0, conflicts: [] };
      const filledCells = Number(progress.filledCells || 0);
      const completion = Math.round((filledCells / 81) * 100);
      // Highlight the perspective (spectator or local)
      const isMe = perspectiveIds.includes(String(playerId));
      return {
        id: playerId,
        username: player.username || 'Player',
        filledCells,
        completion,
        conflictCount: Array.isArray(progress.conflicts) ? progress.conflicts.length : 0,
        isWinner: winners.includes(playerId),
        isMe,
      };
    });
  }, [perspectiveIds, playerProgress, players, winners]);

  const selectedMeta = useMemo(() => {
    if (!selectedCell) return null;
    return {
      row: selectedCell.row,
      col: selectedCell.col,
      boxRow: Math.floor(selectedCell.row / 3),
      boxCol: Math.floor(selectedCell.col / 3),
    };
  }, [selectedCell]);

  useEffect(() => {
    if (!selectedCell) return;
    if (fixedCells[getCellKey(selectedCell.row, selectedCell.col)] || finished) {
      setSelectedCell(null);
    }
  }, [finished, fixedCells, selectedCell]);

  useEffect(() => {
    if (!selectedCell || finished) {
      setFloatingPad(null);
      return undefined;
    }

    const updateFloatingPad = () => {
      const boardElement = boardContainerRef.current;
      const cellElement = cellRefs.current[getCellKey(selectedCell.row, selectedCell.col)];
      if (!boardElement || !cellElement) {
        setFloatingPad(null);
        return;
      }

      const boardRect = boardElement.getBoundingClientRect();
      const padWidth = 148;
      const padHeight = 156;

      // Get selected cell's row, col, and subgrid
      const row = selectedCell.row;
      const col = selectedCell.col;
      const boxRow = Math.floor(row / 3);
      const boxCol = Math.floor(col / 3);

      // Find block corners
      const blockStartRow = boxRow * 3;
      const blockStartCol = boxCol * 3;
      const blockEndRow = blockStartRow + 2;
      const blockEndCol = blockStartCol + 2;
      const blockBottomRightRef = cellRefs.current[getCellKey(blockEndRow, blockEndCol)];
      const blockBottomLeftRef = cellRefs.current[getCellKey(blockEndRow, blockStartCol)];
      const blockTopRightRef = cellRefs.current[getCellKey(blockStartRow, blockEndCol)];
      const blockTopLeftRef = cellRefs.current[getCellKey(blockStartRow, blockStartCol)];
      if (!blockBottomRightRef || !blockBottomLeftRef || !blockTopRightRef || !blockTopLeftRef) {
        setFloatingPad(null);
        return;
      }
      const blockBottomRightRect = blockBottomRightRef.getBoundingClientRect();
      const blockBottomLeftRect = blockBottomLeftRef.getBoundingClientRect();
      const blockTopRightRect = blockTopRightRef.getBoundingClientRect();
      const blockTopLeftRect = blockTopLeftRef.getBoundingClientRect();
      // Board-relative coordinates
      // const blockBottom = blockBottomRightRect.bottom - boardRect.top;
      // const blockRight = blockBottomRightRect.right - boardRect.left;
      // const blockLeft = blockBottomLeftRect.left - boardRect.left;
      // const blockTop = blockTopLeftRect.top - boardRect.top;
      // const blockTopRight = blockTopRightRect.right - boardRect.left;

      // --- Custom alignment logic as per user request ---
      let left, top, align = 'custom';
      // Get cell refs for alignment anchors
      const getCellRect = (r, c) => {
        const ref = cellRefs.current[getCellKey(r, c)];
        return ref ? ref.getBoundingClientRect() : null;
      };


      // Get selected cell's rect for reference
      const selectedRect = getCellRect(row, col);
      const cellWidth = selectedRect ? selectedRect.width : 0;
      // const cellHeight = selectedRect ? selectedRect.height : 0;
      const minDistance = 3 * cellWidth; // at least 3 squares away

      // Vertical alignment
      if (row <= 4) {
        // Top 5 rows: top of keypad at bottom of 6th row
        const anchorRect = getCellRect(5, col);
        top = anchorRect ? (anchorRect.bottom - boardRect.top) : 0;
        // If too close to selected cell, move further down
        if (selectedRect && top - (selectedRect.top - boardRect.top) < minDistance) {
          top = (selectedRect.top - boardRect.top) + minDistance;
        }
      } else {
        // Bottom 5 rows: bottom of keypad at bottom of 3rd row
        const anchorRect = getCellRect(2, col);
        top = anchorRect ? (anchorRect.bottom - boardRect.top - padHeight) : 0;
        // If too close to selected cell, move further up
        if (selectedRect && (selectedRect.bottom - boardRect.top) - (top + padHeight) < minDistance) {
          top = (selectedRect.bottom - boardRect.top) - minDistance - padHeight;
        }
      }

      // Horizontal alignment
      if (col <= 4) {
        // First 5 columns: left of keypad at left of 6th column
        const anchorRect = getCellRect(row, 5);
        left = anchorRect ? (anchorRect.left - boardRect.left) : 0;
        // If too close to selected cell, move further right
        if (selectedRect && left - (selectedRect.left - boardRect.left) < minDistance) {
          left = (selectedRect.left - boardRect.left) + minDistance;
        }
      } else {
        // Last 4 columns: right of keypad at right of 3rd column
        const anchorRect = getCellRect(row, 2);
        left = anchorRect ? (anchorRect.right - boardRect.left - padWidth) : 0;
        // If too close to selected cell, move further left
        if (selectedRect && (selectedRect.right - boardRect.left) - (left + padWidth) < minDistance) {
          left = (selectedRect.right - boardRect.left) - minDistance - padWidth;
        }
      }


    top = 0;

      setFloatingPad({
        direction: align,
        style: {
          left: `${left}px`,
          top: `${top}px`,
          width: `${padWidth}px`,
          opacity: 0.93,
        },
      });
    };

    const animationId = window.requestAnimationFrame(updateFloatingPad);
    window.addEventListener('resize', updateFloatingPad);
    return () => {
      window.cancelAnimationFrame(animationId);
      window.removeEventListener('resize', updateFloatingPad);
    };
  }, [finished, selectedCell]);

  useEffect(() => {
    const lastMove = myProgress?.lastMove || null;
    const moveSignature = lastMove ? JSON.stringify(lastMove) : null;
    if (!moveSignature || moveSignature === lastMoveRef.current) return;
    lastMoveRef.current = moveSignature;

    if (lastMove.status === 'solved') {
      setFeedback({ tone: 'success', text: 'Solved. You completed the grid first.' });
      if (sound) sound.play('ui:positive');
      return;
    }

    if (lastMove.status === 'incorrect_complete') {
      setFeedback({ tone: 'warn', text: 'The grid is full, but it is not solved yet.' });
      if (sound) sound.play('ui:negative');
      return;
    }

    if (Array.isArray(myProgress?.conflicts) && myProgress.conflicts.length > 0) {
      setFeedback({ tone: 'warn', text: 'There are duplicate numbers in a row, column, or box.' });
    } else {
      setFeedback(null);
    }
  }, [myProgress, sound]);

  useEffect(() => {
    if (finished) return undefined;

    const handleKeyDown = (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (selectedCell) {
        const key = getCellKey(selectedCell.row, selectedCell.col);
        if (/^[1-9]$/.test(event.key)) {
          event.preventDefault();
          if (draftMode) {
            setCornerMarks((prev) => {
              const current = prev[key] || [];
              const num = Number(event.key);
              if (current.includes(num)) {
                // Remove small number
                return { ...prev, [key]: current.filter((n) => n !== num) };
              } else if (current.length < 4) {
                // Add small number
                return { ...prev, [key]: [...current, num] };
              } else {
                return prev;
              }
            });
          } else {
            dispatchAction({ type: 'update_cell', row: selectedCell.row, col: selectedCell.col, value: Number(event.key) });
          }
        } else if (event.key === 'Backspace' || event.key === 'Delete' || event.key === '0') {
          event.preventDefault();
          if (draftMode) {
            setCornerMarks((prev) => {
              if (!prev[key]) return prev;
              const next = { ...prev };
              delete next[key];
              return next;
            });
          } else {
            dispatchAction({ type: 'update_cell', row: selectedCell.row, col: selectedCell.col, value: 0 });
          }
        } else if (event.key.toLowerCase() === 'd') {
          event.preventDefault();
          setDraftMode((d) => !d);
        } else if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
          event.preventDefault();
          let { row, col } = selectedCell;
          if (event.key === "ArrowUp") row = (row + 8) % 9;
          if (event.key === "ArrowDown") row = (row + 1) % 9;
          if (event.key === "ArrowLeft") col = (col + 8) % 9;
          if (event.key === "ArrowRight") col = (col + 1) % 9;
          // Skip fixed cells
          let attempts = 0;
          while (fixedCells[getCellKey(row, col)] && attempts < 9) {
            if (event.key === "ArrowUp" || event.key === "ArrowDown") {
              row = event.key === "ArrowUp" ? (row + 8) % 9 : (row + 1) % 9;
            } else {
              col = event.key === "ArrowLeft" ? (col + 8) % 9 : (col + 1) % 9;
            }
            attempts++;
          }
          setSelectedCell({ row, col });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatchAction, finished, selectedCell, fixedCells, draftMode]);


  const handleCellSelect = (row, col) => {
    if (finished) return;
    if (fixedCells[getCellKey(row, col)]) return;
    setSelectedCell({ row, col });
  };

  // Handle number button click
  const handleNumberButton = (num) => {
    if (!selectedCell || finished) return;
    const key = getCellKey(selectedCell.row, selectedCell.col);
    if (draftMode) {
      // In draft mode, ONLY toggle small number in this cell (up to 4), NEVER affect big number
      setCornerMarks((prev) => {
        const current = prev[key] || [];
        if (current.includes(num)) {
          // Remove small number
          return { ...prev, [key]: current.filter((n) => n !== num) };
        } else if (current.length < 4) {
          // Add small number
          return { ...prev, [key]: [...current, num] };
        } else {
          return prev;
        }
      });
      return; // Do not proceed to big number logic
    }
    // Not draft mode: set big number, leave small numbers untouched
    handleNumberPick(num);
  };


  // Remove a corner mark if user clicks orange again (optional, not in spec)
  // Not implemented unless requested

  const handleNumberPick = async (value) => {
    if (!selectedCell || finished) return;
    const response = await dispatchAction({
      type: 'update_cell',
      row: selectedCell.row,
      col: selectedCell.col,
      value,
    });

    if (response?.status === 'fixed_cell') {
      setFeedback({ tone: 'error', text: 'That clue cell cannot be changed.' });
      return;
    }

    if (response?.error || response?.status === 'error') {
      setFeedback({ tone: 'error', text: 'That move could not be applied.' });
    }
    // Do not deselect the cell after a pick
  };


  const showSpectatorPerspectiveSelector = isSpectator && perspectiveOptions.length > 0;

  if (!gameState) {
    return (
      <div className="rounded-3xl bg-white p-6 text-gray-900 shadow-xl dark:bg-slate-900 dark:text-gray-100">
        {showSpectatorPerspectiveSelector && (
          <SpectatorPerspectiveSelector
            players={perspectiveOptions}
            value={spectatorViewPlayerId || ""}
            onChange={setSpectatorViewPlayerId}
          />
        )}
        Loading Sudoku...
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 p-3 text-gray-900 dark:text-gray-100 sm:p-4">
      {showSpectatorPerspectiveSelector && (
        <SpectatorPerspectiveSelector
          players={perspectiveOptions}
          value={spectatorViewPlayerId || ""}
          onChange={setSpectatorViewPlayerId}
        />
      )}
      <div className="overflow-hidden rounded-[28px] border border-cyan-200/70 bg-gradient-to-br from-cyan-50 via-white to-amber-50 shadow-[0_24px_70px_rgba(15,23,42,0.12)] dark:border-cyan-900/60 dark:from-slate-950 dark:via-slate-900 dark:to-cyan-950/80">
        <div className="flex flex-col gap-5 p-4 sm:p-6">
          <div className="space-y-5">
            

            <div className="space-y-5">
              <div className="border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/85 sm:p-5">
                <div ref={boardContainerRef} className="relative w-full flex flex-col items-center">
                  <div
                    className="grid max-w-xs w-full overflow-visible border-[3px] border-slate-900 bg-slate-900 dark:border-slate-400 dark:bg-slate-900 aspect-square"
                    style={{ gridTemplateColumns: 'repeat(9, minmax(0, 1fr))' }}
                  >
                  {grid.map((row, rowIndex) => row.map((value, colIndex) => {
                    const key = getCellKey(rowIndex, colIndex);
                    const isFixed = !!fixedCells[key];
                    const isConflict = conflicts.has(key);
                    const isSelected = selectedCell?.row === rowIndex && selectedCell?.col === colIndex;
                    const isPeer = selectedMeta && !isSelected && (
                      selectedMeta.row === rowIndex ||
                      selectedMeta.col === colIndex ||
                      (selectedMeta.boxRow === Math.floor(rowIndex / 3) && selectedMeta.boxCol === Math.floor(colIndex / 3))
                    );
                    const borderTop = rowIndex === 0 ? 0 : rowIndex % 3 === 0 ? 3 : 1;
                    const borderLeft = colIndex === 0 ? 0 : colIndex % 3 === 0 ? 3 : 1;
                    const corners = cornerMarks[key] || [];
                    // Always show big number if present
                    const showBig = !!value;
                    // Prepare two rows of small numbers (up to 2 per row)
                    const smallTop = corners.slice(0, 2);
                    const smallBottom = corners.slice(2, 4);
                    // Always highlight selected cell blue, even if conflict
                    let cellClass = '';
                    if (isFixed) {
                      cellClass = 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100';
                    } else if (isSelected) {
                      cellClass = 'bg-cyan-300 text-cyan-950 dark:bg-cyan-500 dark:text-slate-950';
                    } else if (isConflict) {
                      cellClass = 'bg-rose-200 text-rose-900 dark:bg-rose-900/70 dark:text-rose-100';
                    } else if (isPeer) {
                      cellClass = 'bg-amber-50 text-slate-900 dark:bg-amber-950/40 dark:text-slate-100';
                    } else {
                      cellClass = 'bg-white text-slate-800 hover:bg-cyan-50 dark:bg-slate-950 dark:text-slate-100 dark:hover:bg-slate-800';
                    }
                    return (
                      <button
                        key={key}
                        type="button"
                        ref={(element) => {
                          if (element) cellRefs.current[key] = element;
                          else delete cellRefs.current[key];
                        }}
                        onClick={() => handleCellSelect(rowIndex, colIndex)}
                        className={`relative aspect-square min-w-0 flex items-center justify-center text-base font-semibold transition-colors sm:text-2xl ${cellClass} ${finished || isFixed ? 'cursor-default' : 'cursor-pointer'}`}
                        style={{
                          borderTop: `${borderTop}px solid ${user && user.darkMode ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.9)'}`,
                          borderLeft: `${borderLeft}px solid ${user && user.darkMode ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.9)'}`,
                        }}
                      >
                        {/* Big number in center if present */}
                        <span>{showBig ? value : ''}</span>
                        {/* Small numbers in two rows, centered, always show even if they match big number */}
                        {(corners.length > 0) && (
                          <span className="absolute left-0 right-0 flex flex-col items-center w-full pointer-events-none select-none" style={{top: 0, bottom: 0, height: '100%'}}>
                            {/* Move top row closer to top edge */}
                            <span
                              className={`flex w-full justify-between text-[0.55em] font-semibold ${isSelected ? 'text-black' : 'text-orange-500'}`}
                              style={{lineHeight:1, position:'absolute', top:'2px', left:0, right:0, paddingLeft:'2px', paddingRight:'2px'}}>
                              {smallTop.map((n, i) => <span key={i + '-' + n}>{n}</span>)}
                            </span>
                            {/* Move bottom row closer to bottom edge */}
                            {smallBottom.length > 0 && (
                              <span
                                className={`flex w-full justify-between text-[0.55em] font-semibold ${isSelected ? 'text-black' : 'text-orange-500'}`}
                                style={{lineHeight:1, position:'absolute', bottom:'2px', left:0, right:0, paddingLeft:'2px', paddingRight:'2px'}}>
                                {smallBottom.map((n, i) => <span key={i + '-' + n}>{n}</span>)}
                              </span>
                            )}
                          </span>
                        )}
                        {isFixed && <span className="absolute inset-x-2 bottom-1 h-[2px] rounded-full bg-slate-400/60 dark:bg-slate-500/70" />}
                      </button>
                    );
                  }))}
                  </div>
                  {/* Fixed keypad rows below the grid, always reserve space for them */}
                  <div className="mt-4 select-none w-full max-w-xs mx-auto rounded-2xl border-2 border-cyan-400/70 shadow-lg bg-white/80 dark:bg-slate-900/80 transition-colors">
                    {/* Row 1: Digits 1-6 */}
                    <div
                      className={`flex justify-center gap-2 rounded-t-xl px-2 py-2 transition ${selectedCell ? (user && user.darkMode ? 'bg-gradient-to-r from-cyan-900 via-blue-900 to-cyan-800' : 'bg-gradient-to-r from-cyan-100 via-blue-100 to-cyan-50') : 'bg-transparent'}`}
                      style={{ minHeight: '32px' }}
                    >
                      {Array.from({ length: 6 }, (_, i) => {
                        const num = i + 1;
                        let btnClass = '';
                        if (!selectedCell || finished) {
                          btnClass = user && user.darkMode ? 'bg-slate-800 text-slate-500 cursor-not-allowed opacity-60' : 'bg-slate-200 text-slate-400 cursor-not-allowed opacity-60';
                        } else {
                          // Highlight if in draft mode and number is in corners, or not in draft and is big number
                          const key = selectedCell ? getCellKey(selectedCell.row, selectedCell.col) : '';
                          const corners = cornerMarks[key] || [];
                          const isBig = grid[selectedCell?.row || 0]?.[selectedCell?.col || 0] === num;
                          if (draftMode && corners.includes(num)) {
                            btnClass = 'bg-orange-400 text-white ring-2 ring-orange-400';
                          } else if (!draftMode && isBig) {
                            btnClass = 'bg-green-500 text-white ring-2 ring-green-400';
                          } else {
                            btnClass = user && user.darkMode ? 'bg-[#23272e] text-cyan-200 hover:bg-cyan-700/80 hover:text-white' : 'bg-white text-blue-700 hover:bg-cyan-200/80 hover:text-blue-900';
                          }
                        }
                        return (
                          <button
                            key={`digit-${num}`}
                            type="button"
                            onClick={() => handleNumberButton(num)}
                            className={`w-8 h-8 rounded-md font-semibold text-sm shadow-sm transition focus:ring-2 focus:ring-cyan-400 ${btnClass}`}
                            disabled={!selectedCell || finished}
                          >
                            {num}
                          </button>
                        );
                      })}
                    </div>
                    {/* Row 2: Digits 7-9 and Pencil/Clear */}
                    <div
                      className={`flex justify-center gap-2 rounded-b-xl px-2 py-2 transition ${selectedCell ? (user && user.darkMode ? 'bg-gradient-to-r from-cyan-900 via-blue-900 to-cyan-800' : 'bg-gradient-to-r from-cyan-100 via-blue-100 to-cyan-50') : 'bg-transparent'}`}
                      style={{ minHeight: '32px' }}
                    >
                      {Array.from({ length: 3 }, (_, i) => {
                        const num = i + 7;
                        let btnClass = '';
                        if (!selectedCell || finished) {
                          btnClass = user && user.darkMode ? 'bg-slate-800 text-slate-500 cursor-not-allowed opacity-60' : 'bg-slate-200 text-slate-400 cursor-not-allowed opacity-60';
                        } else {
                          const key = selectedCell ? getCellKey(selectedCell.row, selectedCell.col) : '';
                          const corners = cornerMarks[key] || [];
                          const isBig = grid[selectedCell?.row || 0]?.[selectedCell?.col || 0] === num;
                          if (draftMode && corners.includes(num)) {
                            btnClass = 'bg-orange-400 text-white ring-2 ring-orange-400';
                          } else if (!draftMode && isBig) {
                            btnClass = 'bg-green-500 text-white ring-2 ring-green-400';
                          } else {
                            btnClass = user && user.darkMode ? 'bg-[#23272e] text-cyan-200 hover:bg-cyan-700/80 hover:text-white' : 'bg-white text-blue-700 hover:bg-cyan-200/80 hover:text-blue-900';
                          }
                        }
                        return (
                          <button
                            key={`digit-${num}`}
                            type="button"
                            onClick={() => handleNumberButton(num)}
                            className={`w-8 h-8 rounded-md font-semibold text-sm shadow-sm transition focus:ring-2 focus:ring-cyan-400 ${btnClass}`}
                            disabled={!selectedCell || finished}
                          >
                            {num}
                          </button>
                        );
                      })}
                      {/* Pencil button to the left of Clear */}
                      <button
                        type="button"
                        onClick={() => setDraftMode((d) => !d)}
                        className={`w-8 h-8 rounded-md font-semibold text-lg shadow-sm transition focus:ring-2 focus:ring-cyan-400 flex items-center justify-center ${draftMode ? 'bg-amber-400 text-white ring-2 ring-amber-400' : user && user.darkMode ? 'bg-[#23272e] text-cyan-200 hover:bg-cyan-700/80 hover:text-white' : 'bg-white text-blue-700 hover:bg-cyan-200/80 hover:text-blue-900'}`}
                        disabled={finished}
                        aria-label="Toggle draft mode"
                        title="Toggle draft mode"
                      >
                        {/* Standard pencil icon */}
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19.5 3 21l1.5-4L16.5 3.5z"/></svg>
                      </button>
                      {/* Orange C: clear small numbers only */}
                      <button
                        type="button"
                        onClick={() => {
                          if (selectedCell && !finished) {
                            const key = getCellKey(selectedCell.row, selectedCell.col);
                            setCornerMarks((prev) => {
                              if (!prev[key]) return prev;
                              const next = { ...prev };
                              delete next[key];
                              return next;
                            });
                          }
                        }}
                        className={`w-8 h-8 rounded-md font-bold text-lg shadow-sm transition focus:ring-2 focus:ring-orange-400 flex items-center justify-center ${
                          selectedCell && (cornerMarks[getCellKey(selectedCell.row, selectedCell.col)]?.length > 0) && !finished
                            ? 'bg-orange-400 text-white ring-2 ring-orange-400'
                            : user && user.darkMode
                              ? selectedCell && !finished
                                ? 'bg-[#23272e] text-orange-400 hover:bg-orange-500/80 hover:text-white'
                                : 'bg-slate-800 text-slate-500 cursor-not-allowed opacity-60'
                              : selectedCell && !finished
                                ? 'bg-white text-orange-500 hover:bg-orange-200/80 hover:text-orange-700'
                                : 'bg-slate-200 text-slate-400 cursor-not-allowed opacity-60'
                        }`}
                        disabled={!selectedCell || finished || !(cornerMarks[getCellKey(selectedCell.row, selectedCell.col)]?.length > 0)}
                        aria-label="Clear small numbers"
                        title="Clear small numbers"
                      >
                        C
                      </button>
                      {/* Green C: clear big number only */}
                      <button
                        type="button"
                        onClick={() => {
                          if (selectedCell && !finished) {
                            handleNumberPick(0);
                          }
                        }}
                        className={`w-8 h-8 rounded-md font-bold text-lg shadow-sm transition focus:ring-2 focus:ring-green-400 flex items-center justify-center ${
                          selectedCell && grid[selectedCell.row][selectedCell.col] !== 0 && !finished
                            ? 'bg-green-500 text-white ring-2 ring-green-400'
                            : user && user.darkMode
                              ? selectedCell && !finished
                                ? 'bg-[#23272e] text-green-400 hover:bg-green-500/80 hover:text-white'
                                : 'bg-slate-800 text-slate-500 cursor-not-allowed opacity-60'
                              : selectedCell && !finished
                                ? 'bg-white text-green-500 hover:bg-green-200/80 hover:text-green-700'
                                : 'bg-slate-200 text-slate-400 cursor-not-allowed opacity-60'
                        }`}
                        disabled={!selectedCell || finished || grid[selectedCell.row][selectedCell.col] === 0}
                        aria-label="Clear big number"
                        title="Clear big number"
                      >
                        C
                      </button>
                    </div>
                  </div>
                  


                </div>
              </div>
            </div>
          </div>

          {/* Player Progress always below the grid */}
          <div className="space-y-5">
            <div className="rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900/85">
              <div className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Player Progress</div>
              <div className="mt-4 space-y-3">
                {progressRows.map((row) => (
                  <div
                    key={row.id}
                    className={`rounded-2xl border px-4 py-3 ${row.isWinner ? 'border-emerald-400 bg-emerald-100/80 dark:border-emerald-700 dark:bg-emerald-950/50' : row.isMe ? 'border-cyan-400 bg-cyan-50 dark:border-cyan-700 dark:bg-cyan-950/40' : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/70'}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold text-slate-900 dark:text-slate-100">{row.username}{row.isMe ? ' (You)' : ''}</div>
                      <div className="text-right">
                        <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">{row.filledCells}/81</div>
                        <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{row.conflictCount} conflicts</div>
                      </div>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                      <div className={`h-full rounded-full ${row.isWinner ? 'bg-emerald-500' : 'bg-cyan-500'}`} style={{ width: `${row.completion}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

SudokuGame.Guide = function SudokuGuide() {
  return (
    <div className="space-y-2 text-sm text-gray-800 dark:text-gray-100">
      <div className="font-bold">How to Play Sudoku</div>
      <div>Every player gets the same 9x9 Sudoku puzzle and solves it on their own board at the same time.</div>
      <div>Fill each row, column, and 3x3 box with the digits 1 through 9 without repeating a number.</div>
      <div>Locked clue cells cannot be changed, and conflicting cells are highlighted if you duplicate a number in a row, column, or box.</div>
      <div>The first player to complete the grid correctly wins the round.</div>
    </div>
  );
};

export default SudokuGame;